import React from "react";
import { Provider } from "react-redux";
import { store } from "./app/stores/store";
import { IndexedDBProvider } from "./providers/indexed-db-provider";
import { LogProvider } from "./providers/log-provider";
import { ThemeProvider } from "./providers/theme-provider";
import { RouterProvider } from "react-router-dom";
import { router } from "./router";

// =============================================
// 1. ErrorBoundary (Same as before, but with dev overlay suppression)
// =============================================
interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false, error: null };

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Suppress React's dev overlay (only in dev mode)
        if (process.env.NODE_ENV === "development") {
            // @ts-ignore - React internal
            window.__reactRefreshOverlayInterceptor?.preventProductionOverlay();
        }
        
    }

    resetError = () => this.setState({ hasError: false, error: null });

    render() {
        if (this.state.hasError) {
            return (
                this.props.fallback || (
                    <div style={{ color: "red", padding: "20px" }}>
                        <h1>Something went wrong.</h1>
                        <pre>{this.state.error?.toString()}</pre>
                        <button onClick={this.resetError}>Try Again</button>
                    </div>
                )
            );
        }
        return this.props.children;
    }
}

const DangerousButton = () => {
    return (
        <button
            onClick={() => {
                throw new Error("Test click error!");
            }}>
            Click Me (Will Crash)
        </button>
    );
};

// =============================================
// 4. App Integration
// =============================================
export const App = () => {
    return (
        <ErrorBoundary>
            <Provider store={store}>
                <DangerousButton />

                <IndexedDBProvider>
                    <LogProvider>
                        <ThemeProvider>
                            <RouterProvider router={router} />
                        </ThemeProvider>
                    </LogProvider>
                </IndexedDBProvider>
            </Provider>
        </ErrorBoundary>
    );
};
